import { Box, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useContext } from 'react'
import { makeStyles } from 'tss-react/mui'
import { SaveChartProps } from '../../../system-data-handler/types'
import { ChartView, RechartData } from 'src/Libs/chart-kit'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import ProductionPieChart from './ProductionPieChart'
import SavingsPieChart from 'src/Libs/dynamic-power-prices/components/cost-savings-chart/SavingsPieChart'
import { useCostSavingsChartData } from 'src/Libs/dynamic-power-prices/hooks/use-cost-savings-chart-data'
import { useSavingsPieChartData } from 'src/Libs/dynamic-power-prices/hooks/use-savings-pie-chart-data'
import { useTypedSelector } from 'src/State/RootReducer'
import useDynamicPricesSettings from 'src/Libs/dynamic-power-prices/hooks/use-dynamic-prices-settings'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import AutarkyPieChart from './AutarkyPieChart'
import { DEFAULT_PIE_CHART_HEIGHT, DEFAULT_PIE_CHART_HEIGHT_BIG } from 'src/Libs/system-charts/default-charts/pie-chart-constants'

export const PIE_CHARTS_BREAKPOINT = 900

const useStyles = makeStyles()(theme => ({
  pieChartContainer: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    gap: theme.spacing(2),
    justifyContent: 'space-between',

    [theme.breakpoints.down(PIE_CHARTS_BREAKPOINT)]: {
      flexDirection: 'column',
    },
  },
  pieChart: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: theme.spacing(2),
  },
  roundedPaper: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper2,
    borderRadius: theme.shape.borderRadius,

    [theme.breakpoints.up(PIE_CHARTS_BREAKPOINT)]: {
      padding: theme.spacing(4),
      paddingBottom: theme.spacing(2),
    },
  },
}))

interface Props {
  data: RechartData
  view: ChartView
  resolution?: ChartView
  handleSaveChart: (props: SaveChartProps) => void
  from: Moment
  to: Moment
  showForecast: boolean
  showSavingsChart?: boolean
  isSavingsChartLoading?: boolean
  isHistoryChartLoading?: boolean
}

/**
 * Pie Charts component with two default pie charts used to be displayed on statistics part of dashboard
 */
const PieCharts: React.FC<Props> = ({
  data,
  view,
  resolution,
  handleSaveChart,
  from,
  to,
  showForecast,
  showSavingsChart,
  isSavingsChartLoading,
  isHistoryChartLoading,
}) => {
  const { classes, cx } = useStyles()
  const theme = useTheme()
  const upSm = useMediaQuery(theme.breakpoints.up(PIE_CHARTS_BREAKPOINT))
  const pieChartHeight = upSm ? DEFAULT_PIE_CHART_HEIGHT_BIG : DEFAULT_PIE_CHART_HEIGHT
  const [legendHeight, setLegendHeight] = React.useState(0)

  const { data: costSavingsData, loadingState } = useCostSavingsChartData(resolution ?? view ?? 'day', from, to, showSavingsChart)
  const { chartData, customTraces, labelTraces } = useSavingsPieChartData({ data: costSavingsData, loadingState })
  const isLoading = isSavingsChartLoading || isHistoryChartLoading

  const storageId = useTypedSelector(state => state.storage.activeStorage) ?? ''
  const { dynamicPricesSettings } = useDynamicPricesSettings({ storageId })
  const { isDynamicPowerPriceEnabled } = useContext(DynamicPowerPricesEnabledContext)

  const isDynamicEmsActive = dynamicPricesSettings?.isDynamicEmsEnabledForBattery

  const setBiggestLegendHeight = useCallback(
    (height: number): void => {
      if (height > legendHeight) {
        setLegendHeight(height)
      }
    },
    [setLegendHeight, legendHeight]
  )

  return (
    <Box className={classes.pieChartContainer}>
      {showSavingsChart && (
        <Box className={cx(classes.pieChart, classes.roundedPaper)}>
          <SavingsPieChart
            customTraces={customTraces}
            labelTraces={labelTraces}
            height={pieChartHeight}
            data={chartData}
            from={from}
            loadingState={{
              state: isLoading ? 0 : 1,
            }}
            to={to}
            showForecast={showForecast}
            view={view}
            resolution={resolution}
            handleSaveChart={handleSaveChart}
            legendHeight={legendHeight}
            setBiggestLegendHeight={setBiggestLegendHeight}
            isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabled}
            isDynamicEmsActive={isDynamicEmsActive}
          />
        </Box>
      )}
      <Box className={cx(classes.pieChart, classes.roundedPaper)}>
        <AutarkyPieChart
          height={pieChartHeight}
          data={data}
          from={from}
          to={to}
          showForecast={showForecast}
          view={view}
          resolution={resolution}
          handleSaveChart={handleSaveChart}
          legendHeight={legendHeight}
          setBiggestLegendHeight={setBiggestLegendHeight}
          isAiActive={isDynamicEmsActive}
        />
      </Box>
      <Box className={cx(classes.pieChart, classes.roundedPaper)}>
        <ProductionPieChart
          height={pieChartHeight}
          data={data}
          from={from}
          to={to}
          showForecast={showForecast}
          view={view}
          resolution={resolution}
          handleSaveChart={handleSaveChart}
          legendHeight={legendHeight}
          setBiggestLegendHeight={setBiggestLegendHeight}
        />
      </Box>
    </Box>
  )
}

export default PieCharts
